import { supabase } from "@/integrations/supabase/client";
import { APP_VERSION } from "@/lib/appVersion";

/**
 * Android APK distribution.
 *
 * The signed APK is uploaded to the public `app-releases` storage bucket so a
 * new build can be shipped without a site redeploy. `checkApkAvailable()` does
 * a lightweight HEAD request so the UI can tell users when a build isn't
 * published yet instead of handing them a broken download.
 */
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;

export const APK_FILE_NAME = "fantasy-football-africa.apk";

export const APK_URL = `${SUPABASE_URL}/storage/v1/object/public/app-releases/android/${APK_FILE_NAME}`;

export async function checkApkAvailable(): Promise<boolean> {
  try {
    const res = await fetch(APK_URL, { method: "HEAD" });
    return res.ok;
  } catch {
    return false;
  }
}

export interface AppRelease {
  version: string;
  version_code: number;
  apk_url: string;
  notes?: string | null;
}

export async function getLatestRelease(): Promise<AppRelease | null> {
  const { data } = await supabase
    .from("app_releases" as any)
    .select("version, version_code, apk_url, notes")
    .eq("platform", "android")
    .eq("is_active", true)
    .order("version_code", { ascending: false })
    .limit(1)
    .maybeSingle();
  return ((data as unknown) as AppRelease) ?? null;
}

/* ------------------------------------------------------------------ */
/* Analytics                                                           */
/* ------------------------------------------------------------------ */

const DEVICE_ID_KEY = "ffa_device_id";
const INSTALL_LOGGED_KEY = "ffa_apk_install_logged";
const OPEN_LOGGED_KEY = "ffa_apk_open_logged_day";

/** Stable anonymous per-device id (no personal data). */
export function getDeviceId(): string {
  try {
    let id = localStorage.getItem(DEVICE_ID_KEY);
    if (!id) {
      id =
        typeof crypto !== "undefined" && "randomUUID" in crypto
          ? crypto.randomUUID()
          : `d-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      localStorage.setItem(DEVICE_ID_KEY, id);
    }
    return id;
  } catch {
    return "unknown";
  }
}

export type ApkEventType = "download_click" | "install" | "app_open";

export interface ApkEventPayload {
  platform?: string | null;
  device_model?: string | null;
  device_manufacturer?: string | null;
  os_version?: string | null;
  app_version?: string | null;
  source?: string | null;
  country?: string | null;
}

/** Fire-and-forget analytics ping. Never throws, never blocks the UI. */
export async function logApkEvent(event: ApkEventType, payload: ApkEventPayload = {}) {
  try {
    await supabase.rpc("log_apk_event" as any, {
      _event_type: event,
      _device_id: getDeviceId(),
      _platform: payload.platform ?? null,
      _device_model: payload.device_model ?? null,
      _device_manufacturer: payload.device_manufacturer ?? null,
      _os_version: payload.os_version ?? null,
      _app_version: payload.app_version ?? APP_VERSION,
      _source: payload.source ?? null,
      _country: payload.country ?? null,
    });
  } catch {
    /* analytics must never break the app */
  }
}

/**
 * Called from the native app. Logs a one-time `install` event per device plus a
 * once-a-day `app_open` heartbeat used for active-device counts.
 */
export async function trackNativeLaunch() {
  let info: ApkEventPayload = {};
  try {
    const { Capacitor } = await import("@capacitor/core");
    info.platform = Capacitor.getPlatform();
    if (Capacitor.isPluginAvailable("Device")) {
      const { Device } = await import("@capacitor/device");
      const d = await Device.getInfo();
      info = {
        ...info,
        device_model: d.model,
        device_manufacturer: d.manufacturer,
        os_version: `${d.operatingSystem} ${d.osVersion}`.trim(),
      };
    }
    if (Capacitor.isPluginAvailable("App")) {
      const { App } = await import("@capacitor/app");
      const appInfo = await App.getInfo();
      info.app_version = appInfo.version;
    }
  } catch {
    /* plugin unavailable — log what we have */
  }

  try {
    if (!localStorage.getItem(INSTALL_LOGGED_KEY)) {
      await logApkEvent("install", info);
      localStorage.setItem(INSTALL_LOGGED_KEY, String(Date.now()));
    }
    const today = new Date().toISOString().slice(0, 10);
    if (localStorage.getItem(OPEN_LOGGED_KEY) !== today) {
      await logApkEvent("app_open", info);
      localStorage.setItem(OPEN_LOGGED_KEY, today);
    }
  } catch {
    /* ignore */
  }
}
